import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { toPipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { getBuildStatusIconComponent } from '@/features/shared/components/tableSystem/buildStatusIcons';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { resolveEntityTags, resolveTagColor } from '@/features/shared/tags';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/suites/components/suitesColumns.latestCellStyles';
import { TableCellContainer } from '@/features/tests/components/testsTableCells';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { Box, formatDuration, formatStatusLabel, Text } from '@/ui';
import {
  getSingleSelectOptionLabel,
  getSingleSelectOptionValue,
} from '@/ui/composites/grid/gridColumns';
import {
  getSingleSelectFilterOperators,
  getStringFilterOperators,
} from '@/ui/composites/grid/gridOperators';
import { RunLinkCell, TableNameCell } from '@/ui/composites/table';
import { resolveIteratorLabel } from '@/ui/composites/table/cellContents/shared';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { resolveStatusChipTone, type GridColDef } from '@/ui/primitives';
import type { TagColor } from '@/shared/data/tags';
import type { BuildDto, MetricThreshold } from '@flowqa/shared';
import type { ReactElement } from 'react';

const columnFlex = {
  run: 0.75,
  pipeline: 1,
  status: 0.75,
  actions: 0.1,
};

type BuildColumnsOptions = {
  showPipelineColumn?: boolean;
  durationThreshold: MetricThreshold;
  tagColorByName?: Partial<Record<string, TagColor>>;
};
export const buildBuildsColumns = ({
  showPipelineColumn = true,
  durationThreshold: _durationThreshold,
  tagColorByName = {},
}: BuildColumnsOptions): Array<GridColDef<BuildDto>> => {
  const stringOperators = getStringFilterOperators();
  const statusOperators = getSingleSelectFilterOperators();

  const runColumn: GridColDef<BuildDto> = {
    field: 'buildKey',
    headerName: 'Run',
    flex: columnFlex.run,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-run-link-cell',
    filterable: false,
    sortable: false,
    valueGetter: (_value, row) => row.buildKey ?? null,
    renderCell: (params): ReactElement => {
      const runNumber = resolveBuildRunNumber(params.row.buildKey);
      const label = runNumber !== null ? resolveIteratorLabel(runNumber) : 'View';
      const isLatest = params.row.isLatest === true;
      const pipelineSlug =
        params.row.pipelineName && params.row.pipelineName.length > 0
          ? toPipelineSlug(params.row.pipelineName)
          : null;
      const pipelineSegment =
        pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (params.row.pipelineId ?? null);
      const href =
        pipelineSegment && runNumber !== null
          ? `/pipelines/${pipelineSegment}/runs/${runNumber}`
          : '/pipelines';
      return (
        <TableCellContainer gap="none">
          <RunLinkCell label={label} href={href} latest={isLatest} />
        </TableCellContainer>
      );
    },
  };

  const pipelineColumn: GridColDef<BuildDto> = {
    field: 'pipelineName',
    headerName: 'Pipeline',
    flex: columnFlex.pipeline,
    cellClassName: 'table-name-cell',
    valueGetter: (_value, row) => row.pipelineName ?? null,
    renderCell: (params): ReactElement => {
      const label = params.row.pipelineName;
      if (!label) {
        return (
          <Text role="body" component="span">
            -
          </Text>
        );
      }
      const pipelineSlug = toPipelineSlug(label);
      const pipelineSegment =
        pipelineSlug.length > 0 ? pipelineSlug : (params.row.pipelineId ?? null);
      return (
        <TableCellContainer gap="none">
          <TableNameCell
            primary={{
              label,
              href: pipelineSegment ? `/pipelines/${pipelineSegment}` : '/pipelines',
            }}
            tags={resolveEntityTags(params.row).map((tag) => ({
              label: tag,
              tone: resolveTagColor(tag, tagColorByName),
            }))}
          />
        </TableCellContainer>
      );
    },
  };
  pipelineColumn.filterOperators = stringOperators;

  const statusColumn: GridColDef<BuildDto> = {
    field: 'status',
    headerName: 'Status',
    flex: columnFlex.status,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-status-meta-cell',
    type: 'singleSelect',
    valueOptions: buildStatusOptions,
    getOptionLabel: getSingleSelectOptionLabel,
    getOptionValue: getSingleSelectOptionValue,
    filterOperators: statusOperators,
    renderCell: (params): ReactElement => {
      const status = params.row.status;
      const label = formatStatusLabel(status);
      const statusTone = resolveStatusChipTone(status);
      const durationMs =
        params.row.startedAt && params.row.finishedAt
          ? Math.max(params.row.finishedAt.getTime() - params.row.startedAt.getTime(), 0)
          : null;
      const durationLabel = formatDuration(durationMs);
      const timestamp = params.row.finishedAt ?? params.row.startedAt ?? null;
      const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
      const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      const runNumber = resolveBuildRunNumber(params.row.buildKey);
      const pipelineSlug =
        params.row.pipelineName && params.row.pipelineName.length > 0
          ? toPipelineSlug(params.row.pipelineName)
          : null;
      const pipelineSegment =
        pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (params.row.pipelineId ?? null);
      const statusHref =
        pipelineSegment && runNumber !== null
          ? `/pipelines/${pipelineSegment}/runs/${runNumber}`
          : null;
      const StatusIcon = getBuildStatusIconComponent(status);

      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={label.length > 0 ? label : '-'}
            statusHref={statusHref}
            chipTone={statusTone}
            chipIcon={<Box component={StatusIcon} />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
            durationLabel={durationLabel}
            durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            timestampLabel={timestampLabel}
            timestampTitle={timestampTitle}
            timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          />
        </TableCellContainer>
      );
    },
  };

  return showPipelineColumn ? [pipelineColumn, runColumn, statusColumn] : [runColumn, statusColumn];
};
